/**
 * React hook for profile form state, dirty-checking, and save logic on the Profile page.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { ToastMessages } from "../components/toasts/toastMessages";
import { updateProfile } from "../services/updateProfileService";
import { useAuthSelector } from "../state/AuthStoreProvider";
import type { AuthRootState } from "../state/store";
import {
  profileDetailsInitialState,
  type ProfileDetails,
} from "../settings/profile/types";
import type { DataState, ProviderWithId } from "../types";

/**
 * Manages profile form state, dirty-checking, and save/update logic.
 *
 * @returns Profile form values, loading/save state, and change handlers
 */
export function useProfileDetails() {
  const { data: providerDetails } = useAuthSelector(
    (state: AuthRootState) => state.providerDetails,
  ) as DataState<ProviderWithId>;

  const [profileDetails, setProfileDetails] = useState<ProfileDetails>(
    profileDetailsInitialState,
  );

  const [profileDetailsInitial, setProfileDetailsInitial] =
    useState<ProfileDetails>(profileDetailsInitialState);

  const [loading, setLoading] = useState(false);
  const [saveButtonDisable, setSaveButtonDisable] = useState(true);

  /**
   * Returns whether any profile field differs from its initial value.
   *
   * @param currentProfileDetails - Current form values to compare
   * @returns `true` when at least one field has changed
   */
  const hasValueChanged = (currentProfileDetails: ProfileDetails): boolean => {
    for (const key in currentProfileDetails) {
      if (currentProfileDetails.hasOwnProperty(key)) {
        if (
          currentProfileDetails[key as keyof ProfileDetails] !==
          profileDetailsInitial[key as keyof ProfileDetails]
        ) {
          return true;
        }
      }
    }
    return false;
  };

  /**
   * Updates first or last name based on the changed input field.
   *
   * @param data - Input change payload with `name` and `value`
   */
  const handleValueChange = (data: { name: string; value: string }) => {
    if (data.name === "profile-first-name") {
      setProfileDetails((prev) => ({
        ...prev,
        firstName: data.value,
      }));
    } else if (data.name === "profile-last-name") {
      setProfileDetails((prev) => ({
        ...prev,
        lastName: data.value,
      }));
    }
  };

  /**
   * Sets the profile picture URL after a successful file upload.
   *
   * @param url - Array of uploaded file URLs; the first entry is used
   */
  const downloadedFilesURL = (url: string[]) => {
    if (url.length > 0) {
      setProfileDetails((prev) => ({
        ...prev,
        profilePicture: url[0],
      }));
    }
  };

  /**
   * Persists the current profile details to the backend.
   *
   * @returns Resolves when the API call completes and loading state is cleared
   */
  const updateProfileDetails = async () => {
    setLoading(true);

    try {
      const response = await updateProfile({
        id: providerDetails?.id || "",
        FirstName: profileDetails.firstName || "",
        LastName: profileDetails.lastName,
        ProfilePicUrl: profileDetails.profilePicture || undefined,
      });

      if (response.success) {
        toast.success(ToastMessages.PROFILE_UPDATE_SUCCESS);
      } else {
        toast.error(response.message || ToastMessages.PROFILE_UPDATE_ERROR);
      }
    } catch (error) {
      toast.error(ToastMessages.PROFILE_UPDATE_ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerDetails) {
      const details: ProfileDetails = {
        firstName: providerDetails?.FirstName || "",
        lastName: providerDetails?.LastName || "",
        email: providerDetails?.EmailAddresses?.[0] || "",
        profilePicture: providerDetails?.ProfilePicUrl || "",
      };
      setProfileDetails(details);
      setProfileDetailsInitial(details);
    }
  }, [providerDetails]);

  useEffect(() => {
    setSaveButtonDisable(!hasValueChanged(profileDetails));
  }, [profileDetails, profileDetailsInitial]);

  return {
    providerDetails,
    profileDetails,
    loading,
    saveButtonDisable,
    handleValueChange,
    downloadedFilesURL,
    updateProfileDetails,
  };
}
